import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toastify } from 'lift-ui';
import { useAuthContext } from '@lift-admin/utils/provider';
import * as api from './api';
import { syncRunPollInterval } from './sync-run-poll';
import { jobsErrorMessage } from './api';
import type { CreateSyncRunsRequest, JobsFilters, SyncRunItemQuery, SyncUsersQuery } from './types';

export const MODULE = 'grapevine' as const;

export const jobsKeys = {
  all: ['grapevine-jobs'] as const,
  overview: () => [...jobsKeys.all, 'overview'] as const,
  jobs: (filters: JobsFilters) => [...jobsKeys.all, 'jobs', filters] as const,
  job: (jobId: string) => [...jobsKeys.all, 'job', jobId] as const,
  queues: () => [...jobsKeys.all, 'queues'] as const,
  workers: (page: number) => [...jobsKeys.all, 'workers', page] as const,
  recurringTasks: () => [...jobsKeys.all, 'recurring-tasks'] as const,
  syncRuns: () => [...jobsKeys.all, 'sync-runs'] as const,
  syncUsers: (query: SyncUsersQuery) => [...jobsKeys.all, 'sync-users', query] as const,
  syncRun: (id: string, query: SyncRunItemQuery) => [...jobsKeys.all, 'sync-run', id, query] as const,
};

/**
 * The gateway is authoritative; these flags only decide what to render. Any
 * action can still come back 403, which the mutations surface as a toast.
 */
export function useJobsAccess() {
  const { canView, canEdit, canManage } = useAuthContext();

  return {
    canView: canView(MODULE),
    canEdit: canEdit(MODULE),
    canManage: canManage(MODULE),
  };
}

export function useOverview() {
  return useQuery({ queryKey: jobsKeys.overview(), queryFn: api.getOverview });
}

export function useJobs(filters: JobsFilters) {
  return useQuery({ queryKey: jobsKeys.jobs(filters), queryFn: () => api.getJobs(filters) });
}

export function useJob(jobId: string | null) {
  return useQuery({
    queryKey: jobsKeys.job(jobId ?? ''),
    queryFn: () => api.getJob(jobId as string),
    enabled: Boolean(jobId),
  });
}

export function useQueues() {
  return useQuery({ queryKey: jobsKeys.queues(), queryFn: api.getQueues });
}

export function useWorkers(page: number) {
  return useQuery({ queryKey: jobsKeys.workers(page), queryFn: () => api.getWorkers(page) });
}

export function useRecurringTasks() {
  return useQuery({ queryKey: jobsKeys.recurringTasks(), queryFn: api.getRecurringTasks });
}

export function useSyncRuns(enabled: boolean) {
  return useQuery({
    queryKey: jobsKeys.syncRuns(),
    queryFn: () => api.getSyncRuns(),
    enabled,
    refetchInterval: (query) => syncRunPollInterval((query.state.data?.data ?? []).map((run) => run.status)),
  });
}

export function useSyncRun(id: string | null, query: SyncRunItemQuery) {
  return useQuery({
    queryKey: jobsKeys.syncRun(id ?? '', query),
    queryFn: () => api.getSyncRun(id as string, query),
    enabled: Boolean(id),
    placeholderData: (previousData, previousQuery) => {
      const previousId = previousQuery?.queryKey[2];
      return previousId === id ? previousData : undefined;
    },
    refetchInterval: (query) => syncRunPollInterval(query.state.data ? [query.state.data.status] : []),
  });
}

export function useSyncUsers(query: SyncUsersQuery, enabled = true) {
  return useQuery({
    queryKey: jobsKeys.syncUsers(query),
    queryFn: () => api.getSyncUsers(query),
    enabled,
    // Keep the previous rows on screen only while paging the same search.
    placeholderData: (previousData, previousQuery) => {
      const previous = previousQuery?.queryKey[2] as SyncUsersQuery | undefined;
      const same = previous?.type === query.type && previous?.q === query.q && previous?.mode === query.mode;
      return same ? previousData : undefined;
    },
  });
}

export function useResolveSyncEmails() {
  return useMutation({ mutationFn: (emails: string[]) => api.resolveSyncEmails(emails) });
}

export function useSyncRunZip() {
  return useMutation({ mutationFn: (id: string) => api.getSyncRunZip(id) });
}

export function useCreateSyncRuns() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateSyncRunsRequest) => api.createSyncRuns(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsKeys.syncRuns() });
      queryClient.invalidateQueries({ queryKey: [...jobsKeys.all, 'sync-run'] });
      queryClient.invalidateQueries({ queryKey: [...jobsKeys.all, 'sync-users'] });
    },
    onError: (error: unknown) => {
      Toastify('error', jobsErrorMessage(error, 'Could not start the sync'));
    },
  });
}

/**
 * Shared wiring for every operational action: toast the server's message on
 * success, toast a readable reason on failure, and refetch everything the
 * action could have changed.
 */
function useJobsMutation<TArgs, TResult extends { message: string }>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  fallbackError: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      Toastify('success', result.message);
      queryClient.invalidateQueries({ queryKey: jobsKeys.all });
    },
    onError: (error: unknown) => {
      Toastify('error', jobsErrorMessage(error, fallbackError));
    },
  });
}

export const useRetryJob = () => useJobsMutation(api.retryJob, 'Could not retry the job');
export const useDispatchJob = () => useJobsMutation(api.dispatchJob, 'Could not dispatch the job');
export const useDiscardJob = () => useJobsMutation(api.discardJob, 'Could not discard the job');
export const useRetryAll = () => useJobsMutation(api.retryAll, 'Could not retry the failed jobs');
export const useDiscardAll = () =>
  useJobsMutation(api.discardAll, 'Could not discard the failed jobs');
export const usePauseQueue = () => useJobsMutation(api.pauseQueue, 'Could not pause the queue');
export const useResumeQueue = () => useJobsMutation(api.resumeQueue, 'Could not resume the queue');
export const useRunRecurringTask = () =>
  useJobsMutation(api.runRecurringTask, 'Could not run the recurring task');
